// src/features/userManagement/UserManagementPage.jsx
//
// Merchant sub-user management page.
// Merchants can create, view, edit, activate/deactivate, and delete users
// within their own organization (tenant-isolated via organizationId from JWT).

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import UserTable from './components/UserTable';
import InviteUserModal from './components/InviteUserModal';
import EditUserModal from './components/EditUserModal';
import {
  listOrgUsers,
  createOrgUser,
  updateOrgUser,
  updateOrgUserStatus,
  deleteOrgUser,
} from './services/userManagementService';
import { setUserScreens } from './services/screenService';

// Must match backend UserStatus enum: 1=ACTIVE 2=PENDING 3=PENDING_FOR_KYC 4=INACTIVE 5=REJECTED
const STATUS_LABELS = {
  1: 'Active',
  2: 'Pending',
  3: 'Pending KYC',
  4: 'Inactive',
  5: 'Rejected',
};

export default function UserManagementPage() {
  const organizationId = useSelector((state) => state.auth?.organizationId);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  // For inline status update (shown as a small dropdown confirmation)
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState(1);

  const fetchUsers = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const data = await listOrgUsers(organizationId);
      setUsers(data);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to load users';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (request) => {
    if (!organizationId) return;
    setSubmitting(true);
    try {
      await createOrgUser(organizationId, request);
      toast.success('User created successfully');
      setCreateOpen(false);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (clientId, request, screens) => {
    if (!organizationId) return;
    setSubmitting(true);
    try {
      await updateOrgUser(organizationId, clientId, request);
      if (Array.isArray(screens)) {
        await setUserScreens(organizationId, clientId, screens);
      }
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!organizationId || !statusTarget) return;
    setSubmitting(true);
    try {
      await updateOrgUserStatus(organizationId, statusTarget.client_id, { user_status_id: statusValue });
      toast.success('Status updated');
      setStatusTarget(null);
      fetchUsers();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update status';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (user) => {
    if (!organizationId) return;
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) return;
    setSubmitting(true);
    try {
      await deleteOrgUser(organizationId, user.client_id);
      toast.success('User deleted');
      setUsers(prev => prev.filter(u => u.client_id !== user.client_id));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-500">Your account is not associated with an organization.</p>
          <p className="text-gray-400 text-sm mt-1">Contact your administrator to assign you to an org.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sub-User Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage users within your organization · {users.length} user{users.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            + Invite User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 mb-1">Total Users</p>
          <p className="text-2xl font-bold text-gray-900">{users.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 mb-1">Active</p>
          <p className="text-2xl font-bold text-green-700">{users.filter(u => u.user_status_id === 1).length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500 mb-1">Inactive / Rejected</p>
          <p className="text-2xl font-bold text-amber-700">
            {users.filter(u => u.user_status_id === 4 || u.user_status_id === 5).length}
          </p>
        </div>
      </div>

      {/* Inline Status Update Panel */}
      {statusTarget && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-4">
          <span className="text-sm text-blue-800 font-medium">Update status for <strong>{statusTarget.username}</strong>:</span>
          <select
            value={statusValue}
            onChange={e => setStatusValue(Number(e.target.value))}
            className="rounded border border-blue-300 px-2 py-1 text-sm bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {Object.entries(STATUS_LABELS).map(([id, label]) => (
              <option key={id} value={Number(id)}>{label}</option>
            ))}
          </select>
          <button
            onClick={handleUpdateStatus}
            disabled={submitting}
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Updating...' : 'Apply'}
          </button>
          <button
            onClick={() => setStatusTarget(null)}
            className="text-blue-600 text-sm hover:underline"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Table */}
      <UserTable
        users={users}
        loading={loading}
        onEdit={(user) => setEditUser(user)}
        onUpdateStatus={(user) => { setStatusTarget(user); setStatusValue(user.user_status_id); }}
        onDelete={handleDelete}
      />

      {/* Modals */}
      <InviteUserModal
        open={createOpen}
        submitting={submitting}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      <EditUserModal
        open={!!editUser}
        user={editUser}
        organizationId={organizationId}
        submitting={submitting}
        onClose={() => setEditUser(null)}
        onSubmit={handleEdit}
      />
    </div>
  );
}
